import { useCallback, useRef, useState } from 'react';
import { theme } from 'antd';
import {
  getHorizontalIndicatorStyle,
  getVerticalIndicatorStyle,
  resolveHorizontalDropIndex,
  resolveVerticalDropIndex,
} from '../utils/sortableDropUtils';

const ORIENTATION = {
  horizontal: {
    resolveDropIndex: (e, itemRefs) => resolveHorizontalDropIndex(e.clientX, e.clientY, itemRefs),
    getIndicatorStyle: getHorizontalIndicatorStyle,
  },
  vertical: {
    resolveDropIndex: (e, itemRefs) => resolveVerticalDropIndex(e.clientY, itemRefs),
    getIndicatorStyle: getVerticalIndicatorStyle,
  },
};

function computeInsertIndex(fromIndex, toIndex) {
  let insertAt = toIndex;
  if (fromIndex < insertAt) insertAt -= 1;
  return insertAt;
}

/**
 * Native HTML5 list reorder with pointer hit-testing and an absolute insertion indicator.
 * onReorder(fromIndex, insertAt) receives indices after insert-position adjustment.
 */
export default function useNativeSortableList({
  orientation = 'horizontal',
  disabled = false,
  onReorder,
}) {
  const { token } = theme.useToken();
  const config = ORIENTATION[orientation] ?? ORIENTATION.horizontal;

  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const dragIndexRef = useRef(null);
  const dragCleanupRef = useRef(null);
  const dragEndTimerRef = useRef(null);

  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const finishDrag = useCallback(() => {
    if (dragEndTimerRef.current != null) {
      clearTimeout(dragEndTimerRef.current);
      dragEndTimerRef.current = null;
    }
    dragCleanupRef.current?.();
    dragCleanupRef.current = null;
    dragIndexRef.current = null;
    setDragIndex(null);
    setDropIndex(null);
  }, []);

  const handleDragStart = useCallback((index, e) => {
    if (disabled) return;
    e.stopPropagation();
    dragIndexRef.current = index;
    setDragIndex(index);
    setDropIndex(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    try {
      e.dataTransfer.setDragImage(e.currentTarget, 8, 8);
    } catch {
      /* ignore */
    }
    const enableDrop = (ev) => {
      ev.preventDefault();
      ev.dataTransfer.dropEffect = 'move';
    };
    document.addEventListener('dragover', enableDrop);
    dragCleanupRef.current = () => document.removeEventListener('dragover', enableDrop);
  }, [disabled]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDropIndex(null);
    dragEndTimerRef.current = setTimeout(() => finishDrag(), 50);
  }, [finishDrag]);

  const performDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = dragIndexRef.current ?? Number(e.dataTransfer.getData('text/plain'));
    if (disabled || Number.isNaN(fromIndex)) {
      finishDrag();
      return;
    }
    const toIndex = config.resolveDropIndex(e, itemRefs);
    const insertAt = computeInsertIndex(fromIndex, toIndex);
    if (fromIndex === insertAt) {
      finishDrag();
      return;
    }
    onReorder?.(fromIndex, insertAt);
    finishDrag();
  }, [config, disabled, finishDrag, onReorder]);

  const handleContainerDragOver = useCallback((e) => {
    if (dragIndexRef.current == null || disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(config.resolveDropIndex(e, itemRefs));
  }, [config, disabled]);

  const handleContainerDrop = useCallback((e) => {
    if (dragIndexRef.current == null || disabled) return;
    performDrop(e);
  }, [performDrop, disabled]);

  const setItemRef = useCallback((index) => (el) => {
    itemRefs.current[index] = el;
  }, []);

  const getHandleProps = useCallback((index) => ({
    onDragStart: (e) => handleDragStart(index, e),
    onDragEnd: handleDragEnd,
  }), [handleDragEnd, handleDragStart]);

  const indicatorStyle = dragIndex != null && !disabled && dropIndex != null && containerRef.current
    ? config.getIndicatorStyle(dropIndex, itemRefs, containerRef.current)
    : null;

  const containerProps = {
    ref: containerRef,
    onDragOver: handleContainerDragOver,
    onDrop: handleContainerDrop,
  };

  const insertionIndicator = indicatorStyle ? {
    style: {
      position: 'absolute',
      left: indicatorStyle.left,
      top: indicatorStyle.top,
      width: indicatorStyle.width,
      height: indicatorStyle.height,
      borderRadius: 2,
      background: token.colorPrimary,
      pointerEvents: 'none',
      zIndex: 2,
    },
  } : null;

  return {
    dragIndex,
    containerProps,
    setItemRef,
    getHandleProps,
    insertionIndicator,
  };
}
